import {
  CalendarIcon,
  CallHospitalIcon,
  ClinicLocationIcon,
  MailOutlineIcon,
  NewStethoScope,
} from "../../../../components/Icons";

import React from "react";
import { UrgentVisitBookingResponse } from "../../../../state/urgent-visit";
import UrgentVisitIcon from "../../../../components/Icons/UrgentVisitIcon";
import { fromUnixToFullDateNum, isTodayInTimeZone } from "../../../../utils/unix-timestamp";
import { getHourFromUnix } from "../../../../utils";
import { formatDoctorNames } from "../../../../utils/genericUtils";

interface BookedDetailsCardProps {
  booking: UrgentVisitBookingResponse;
}

export const BookedDetailsCard = ({ booking }: BookedDetailsCardProps) => {
  const timeFormatted = getHourFromUnix(
    booking.start_at, booking.location.timeZone,
  );
  const dateObj = fromUnixToFullDateNum(booking.start_at, booking.location.timeZone);
  const todayDate = `${dateObj.month} ${dateObj.day}`;
  const isToday = isTodayInTimeZone(booking.start_at, booking.location.timeZone);
  const parseReasonForVisit = (raw: string) => {
    const match = raw.match(/Description:\s?([\s\S]*?),reasons:\s?([\s\S]*)/);
    if (match) return { description: match[1], reasons: match[2] };
    return { description: "", reasons: raw };
  };
  const isMultiPet = booking.pets.length > 1;
  const petReasonsData = booking.pets.map((p) => ({
    name: p.name,
    ...parseReasonForVisit(p.reasonForVisit || ""),
  }));
  const doctorName = formatDoctorNames(booking.resources) || "Doctor";

  return (
    <div className="bg-white border border-taupe-50 rounded-xl p-4
      flex flex-col gap-4"
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-3 items-center">
          <CalendarIcon width={24} height={24} color="#262626" />
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex gap-1 items-center flex-wrap
              font-semibold text-base text-light-black leading-6"
            >
              <span>{isToday ? "Today" : dateObj.weekday}</span>
              <span className="text-gray-4">{"|"}</span>
              <span className="whitespace-nowrap">{todayDate} · {timeFormatted}</span>
            </div>
            <div className="h-px bg-gv-yellow" />
          </div>
        </div>

        <div className="flex gap-3 items-start">
          <ClinicLocationIcon width={24} height={24} color="#262626" />
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-base text-light-black leading-6">
              GoodVets {booking.location.locationName}
            </span>
            <span className="text-sm text-light-black leading-5">
              {booking.location.address}
            </span>
          </div>
        </div>
      </div>

      <hr className="border-gray-4" />

      <div className="flex flex-col gap-3">
        {petReasonsData.map((pet, index) => (
          <div key={index} className="flex gap-3 items-start">
            <div className="bg-taupe-4 rounded-full p-[4.8px]
              w-6 h-6 flex items-center justify-center shrink-0"
            >
              <UrgentVisitIcon width={16} height={16} />
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              {isMultiPet && (
                <p className="text-xs text-gray-1">{pet.name}</p>
              )}
              <p className="geograph-regular text-base text-light-black leading-6 break-words">
                <span className="font-semibold">
                  {"Urgent Visit:"}
                </span>{" "}
                {pet.reasons}
              </p>
              {pet.description && (
                <p className="geograph-regular text-base font-medium leading-6 text-[#262626] break-words">
                  <span className="font-semibold">{"Reason: "}</span>
                  {pet.description}
                </p>
              )}
            </div>
          </div>
        ))}

        <div className="flex gap-3 items-center">
          <NewStethoScope />
          <span className="font-semibold text-base text-light-black leading-6">
            {doctorName}
          </span>
        </div>
      </div>

      <hr className="border-gray-4" />

      {(booking.location.email || booking.location.phoneNumber) && (
        <div className="flex flex-col gap-3">
          <p className="font-medium text-base text-gray-1 leading-5">
            {"Hospital Contact"}
          </p>
          {booking.location.email && (
            <div className="flex gap-3 items-center">
              <MailOutlineIcon />
              <span className="font-semibold text-base text-light-black leading-6 break-words">
                {booking.location.email}
              </span>
            </div>
          )}
          {booking.location.phoneNumber && (
            <div className="flex gap-3 items-center">
              <CallHospitalIcon />
              <span className="font-semibold text-base text-light-black leading-6 break-words">
                {booking.location.phoneNumber}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
